import { useState } from 'react';
import { useSEO } from '../../utils/seo';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ConsultationModal from '../../components/feature/ConsultationModal';

export default function ConsultingPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  useSEO({
    title: 'Consulting Services - DevOps, Cybersecurity, Blockchain & Monitoring',
    description: 'Expert consulting services in DevOps automation, cybersecurity, crypto market blockchain adoption, and observability monitoring. Transform your business with 20+ years of industry expertise.',
    keywords: 'DevOps consulting, cybersecurity services, blockchain consulting, observability monitoring, automation services',
    canonical: `${siteUrl}/consulting`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'ProfessionalService',
      name: 'ProtectWize Consulting Services',
      url: `${siteUrl}/consulting`,
      description: 'Expert data analytics and business intelligence consulting services',
      provider: {
        '@type': 'Organization',
        name: 'ProtectWize'
      },
      areaServed: 'Worldwide',
      serviceType: 'Data Analytics Consulting',
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Consulting Services',
        itemListElement: [
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'DevOps and Automation',
              description: 'CI/CD pipelines, Infrastructure as Code, container orchestration'
            }
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Cybersecurity',
              description: 'Security audits, penetration testing, compliance guidance'
            }
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Crypto Market and Blockchain Adoption',
              description: 'Blockchain strategy, crypto market analysis, smart contracts'
            }
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Observability and Monitoring',
              description: 'Full-stack observability, metrics, logs, traces, custom dashboards'
            }
          }
        ]
      }
    }
  });

  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-950">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-[70vh] flex items-center pt-20"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/50d5bdb2ae1bba7db573b7d694eb8519.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-gray-950/95 via-gray-900/85 to-gray-950/90"></div>
        
        <div className="relative w-full max-w-7xl mx-auto px-6 lg:px-16 py-24">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/20 backdrop-blur-sm rounded-full border border-teal-400/30 mb-8">
              <i className="ri-briefcase-line text-teal-400"></i>
              <span className="text-sm text-teal-300">Expert Consulting Services</span>
            </div>
            
            <h1 className="text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Transform Your Business with Expert Technology Consulting
            </h1>
            
            <p className="text-xl lg:text-2xl text-gray-300 leading-relaxed mb-10">
              Leverage our 20+ years of IT expertise to accelerate your digital transformation, enhance security, and optimize operations with cutting-edge technology solutions.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <button 
                onClick={() => {
                  const servicesSection = document.getElementById('services');
                  servicesSection?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="px-8 py-4 bg-teal-500 text-gray-900 rounded-lg font-semibold hover:bg-teal-400 transition-all cursor-pointer whitespace-nowrap shadow-xl text-lg"
              >
                Explore Our Services
              </button>
              <button 
                onClick={() => setIsModalOpen(true)}
                className="px-8 py-4 bg-transparent text-white rounded-lg font-semibold border-2 border-teal-500 hover:bg-teal-500/10 transition-all cursor-pointer whitespace-nowrap text-lg"
              >
                Schedule a Consultation
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Our Consulting */}
      <section className="py-20 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-16">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Why Choose ProtectWize Consulting?
            </h2>
            <p className="text-gray-400 text-lg max-w-3xl mx-auto">
              Partner with a team that combines deep technical expertise with real-world business acumen to deliver measurable results.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700 hover:border-teal-500/50 transition-all text-center">
              <div className="w-16 h-16 bg-teal-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-medal-line text-4xl text-teal-400"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">20+ Years Experience</h3>
              <p className="text-gray-400">
                Decades of hands-on expertise in IT infrastructure, automation, and cybersecurity.
              </p>
            </div>

            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700 hover:border-teal-500/50 transition-all text-center">
              <div className="w-16 h-16 bg-teal-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-4xl text-teal-400"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Security-First Approach</h3>
              <p className="text-gray-400">
                Postgraduate-level cybersecurity expertise ensuring your systems are protected.
              </p>
            </div>

            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700 hover:border-teal-500/50 transition-all text-center">
              <div className="w-16 h-16 bg-teal-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-rocket-line text-4xl text-teal-400"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Proven Results</h3>
              <p className="text-gray-400">
                Track record of successful implementations across diverse industries and technologies.
              </p>
            </div>

            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700 hover:border-teal-500/50 transition-all text-center">
              <div className="w-16 h-16 bg-teal-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <i className="ri-customer-service-line text-4xl text-teal-400"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Dedicated Support</h3>
              <p className="text-gray-400">
                Personalized consulting tailored to your unique business needs and objectives.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gray-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-16">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Our Consulting Services
            </h2>
            <p className="text-gray-400 text-lg max-w-3xl mx-auto">
              Comprehensive technology consulting services designed to drive innovation, enhance security, and optimize your business operations.
            </p>
          </div>

          {/* DevOps and Automation */}
          <div className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/20 backdrop-blur-sm rounded-full border border-teal-400/30 mb-6">
                  <i className="ri-settings-3-line text-teal-400"></i>
                  <span className="text-sm text-teal-300">DevOps</span>
                </div>
                
                <h3 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                  DevOps &amp; Automation
                </h3>
                
                <p className="text-gray-300 text-lg leading-relaxed mb-6">
                  Accelerate your software delivery and improve operational efficiency with our comprehensive DevOps and automation consulting. We help organizations streamline their development pipelines, reduce manual processes, and achieve faster time-to-market while maintaining quality and reliability.
                </p>

                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-teal-500/20 rounded-lg flex items-center justify-center mt-1 flex-shrink-0">
                      <i className="ri-check-line text-teal-400 text-sm"></i>
                    </div>
                    <div>
                      <strong className="text-white">CI/CD Pipeline Design &amp; Implementation</strong>
                      <p className="text-gray-400 text-sm mt-1">Build automated deployment pipelines that reduce errors and accelerate releases.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-teal-500/20 rounded-lg flex items-center justify-center mt-1 flex-shrink-0">
                      <i className="ri-check-line text-teal-400 text-sm"></i>
                    </div>
                    <div>
                      <strong className="text-white">Infrastructure as Code (IaC)</strong>
                      <p className="text-gray-400 text-sm mt-1">Automate infrastructure provisioning with Terraform, Ansible, and CloudFormation.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-teal-500/20 rounded-lg flex items-center justify-center mt-1 flex-shrink-0">
                      <i className="ri-check-line text-teal-400 text-sm"></i>
                    </div>
                    <div>
                      <strong className="text-white">Container Orchestration</strong>
                      <p className="text-gray-400 text-sm mt-1">Deploy and manage containerized applications with Kubernetes and Docker.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-teal-500/20 rounded-lg flex items-center justify-center mt-1 flex-shrink-0">
                      <i className="ri-check-line text-teal-400 text-sm"></i>
                    </div>
                    <div>
                      <strong className="text-white">Process Automation &amp; Optimization</strong>
                      <p className="text-gray-400 text-sm mt-1">Eliminate repetitive tasks and improve team productivity through intelligent automation.</p>
                    </div>
                  </div>
                </div>

                <button 
                  onClick={() => setIsModalOpen(true)}
                  className="px-6 py-3 bg-teal-500 text-gray-900 rounded-lg font-semibold hover:bg-teal-400 transition-all cursor-pointer whitespace-nowrap"
                >
                  Learn More
                </button>
              </div>

              <div className="order-1 lg:order-2">
                <img 
                  src="https://readdy.ai/api/search-image?query=Modern%20DevOps%20automation%20workspace%20with%20multiple%20monitors%20displaying%20CI%2FCD%20pipelines%2C%20infrastructure%20as%20code%20configurations%2C%20Kubernetes%20dashboards%2C%20Docker%20containers%20visualization%2C%20automated%20deployment%20workflows%2C%20code%20repositories%2C%20Jenkins%20or%20GitLab%20CI%20interfaces%2C%20professional%20tech%20environment%20with%20teal%20accent%20lighting%2C%20clean%20organized%20setup%2C%20high-tech%20atmosphere&width=800&height=600&seq=devops-automation-service-001&orientation=landscape"
                  alt="DevOps and Automation"
                  className="rounded-2xl shadow-2xl w-full h-[500px] object-cover object-top"
                />
              </div>
            </div>
          </div>

          {/* Cybersecurity */}
          <div className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <img 
                  src="https://readdy.ai/api/search-image?query=Advanced%20cybersecurity%20operations%20center%20with%20security%20monitoring%20dashboards%2C%20threat%20detection%20systems%2C%20firewall%20configurations%2C%20vulnerability%20scanning%20tools%2C%20network%20security%20visualization%2C%20intrusion%20detection%20displays%2C%20security%20analytics%20graphs%2C%20professional%20SOC%20environment%20with%20teal%20and%20cyan%20accent%20lighting%2C%20high-tech%20security%20command%20center%2C%20sophisticated%20atmosphere&width=800&height=600&seq=cybersecurity-service-002&orientation=landscape"
                  alt="Cybersecurity"
                  className="rounded-2xl shadow-2xl w-full h-[500px] object-cover object-top"
                />
              </div>

              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/20 backdrop-blur-sm rounded-full border border-teal-400/30 mb-6">
                  <i className="ri-shield-check-line text-teal-400"></i>
                  <span className="text-sm text-teal-300">Cybersecurity</span>
                </div>
                
                <h3 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                  Cybersecurity
                </h3>
                
                <p className="text-gray-300 text-lg leading-relaxed mb-6">
                  Protect your digital assets with enterprise-grade cybersecurity consulting backed by postgraduate expertise. We provide comprehensive security assessments, implementation strategies, and ongoing protection to safeguard your business from evolving cyber threats.
                </p>

                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-teal-500/20 rounded-lg flex items-center justify-center mt-1 flex-shrink-0">
                      <i className="ri-check-line text-teal-400 text-sm"></i>
                    </div>
                    <div>
                      <strong className="text-white">Security Audits &amp; Assessments</strong>
                      <p className="text-gray-400 text-sm mt-1">Comprehensive evaluation of your security posture with actionable recommendations.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-teal-500/20 rounded-lg flex items-center justify-center mt-1 flex-shrink-0">
                      <i className="ri-check-line text-teal-400 text-sm"></i>
                    </div>
                    <div>
                      <strong className="text-white">Penetration Testing &amp; Vulnerability Management</strong>
                      <p className="text-gray-400 text-sm mt-1">Identify and remediate security weaknesses before attackers can exploit them.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-teal-500/20 rounded-lg flex items-center justify-center mt-1 flex-shrink-0">
                      <i className="ri-check-line text-teal-400 text-sm"></i>
                    </div>
                    <div>
                      <strong className="text-white">Web Application Security</strong>
                      <p className="text-gray-400 text-sm mt-1">Secure your web applications against OWASP Top 10 and emerging threats.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-teal-500/20 rounded-lg flex items-center justify-center mt-1 flex-shrink-0">
                      <i className="ri-check-line text-teal-400 text-sm"></i>
                    </div>
                    <div>
                      <strong className="text-white">Compliance &amp; Regulatory Guidance</strong>
                      <p className="text-gray-400 text-sm mt-1">Navigate complex compliance requirements including GDPR, HIPAA, and PCI-DSS.</p>
                    </div>
                  </div>
                </div>

                <button 
                  onClick={() => setIsModalOpen(true)}
                  className="px-6 py-3 bg-teal-500 text-gray-900 rounded-lg font-semibold hover:bg-teal-400 transition-all cursor-pointer whitespace-nowrap"
                >
                  Learn More
                </button>
              </div>
            </div>
          </div>

          {/* Crypto Market and Blockchain */}
          <div className="mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/20 backdrop-blur-sm rounded-full border border-teal-400/30 mb-6">
                  <i className="ri-links-line text-teal-400"></i>
                  <span className="text-sm text-teal-300">Crypto</span>
                </div>
                
                <h3 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                  Crypto Market &amp; Blockchain Adoption
                </h3>
                
                <p className="text-gray-300 text-lg leading-relaxed mb-6">
                  Navigate the complex world of cryptocurrency and blockchain technology with expert guidance. We help businesses understand, evaluate, and implement blockchain solutions while providing strategic insights into crypto market dynamics and investment opportunities.
                </p>

                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-teal-500/20 rounded-lg flex items-center justify-center mt-1 flex-shrink-0">
                      <i className="ri-check-line text-teal-400 text-sm"></i>
                    </div>
                    <div>
                      <strong className="text-white">Blockchain Strategy &amp; Implementation</strong>
                      <p className="text-gray-400 text-sm mt-1">Develop and execute blockchain adoption strategies tailored to your business model.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-teal-500/20 rounded-lg flex items-center justify-center mt-1 flex-shrink-0">
                      <i className="ri-check-line text-teal-400 text-sm"></i>
                    </div>
                    <div>
                      <strong className="text-white">Crypto Market Analysis</strong>
                      <p className="text-gray-400 text-sm mt-1">Data-driven insights and analysis to inform your cryptocurrency investment decisions.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-teal-500/20 rounded-lg flex items-center justify-center mt-1 flex-shrink-0">
                      <i className="ri-check-line text-teal-400 text-sm"></i>
                    </div>
                    <div>
                      <strong className="text-white">Smart Contract Development &amp; Auditing</strong>
                      <p className="text-gray-400 text-sm mt-1">Build secure, efficient smart contracts and audit existing implementations.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-teal-500/20 rounded-lg flex items-center justify-center mt-1 flex-shrink-0">
                      <i className="ri-check-line text-teal-400 text-sm"></i>
                    </div>
                    <div>
                      <strong className="text-white">DeFi &amp; Web3 Integration</strong>
                      <p className="text-gray-400 text-sm mt-1">Leverage decentralized finance and Web3 technologies for innovative business solutions.</p>
                    </div>
                  </div>
                </div>

                <button 
                  onClick={() => setIsModalOpen(true)}
                  className="px-6 py-3 bg-teal-500 text-gray-900 rounded-lg font-semibold hover:bg-teal-400 transition-all cursor-pointer whitespace-nowrap"
                >
                  Learn More
                </button>
              </div>

              <div className="order-1 lg:order-2">
                <img 
                  src="https://readdy.ai/api/search-image?query=Cryptocurrency%20and%20blockchain%20technology%20workspace%20with%20multiple%20screens%20displaying%20crypto%20market%20charts%2C%20blockchain%20network%20visualization%2C%20smart%20contract%20code%2C%20DeFi%20protocols%2C%20Web3%20applications%2C%20cryptocurrency%20trading%20platforms%2C%20blockchain%20nodes%20and%20transactions%2C%20digital%20wallet%20interfaces%2C%20professional%20fintech%20environment%20with%20teal%20accent%20lighting%2C%20modern%20sophisticated%20setup&width=800&height=600&seq=crypto-blockchain-service-003&orientation=landscape"
                  alt="Crypto Market and Blockchain"
                  className="rounded-2xl shadow-2xl w-full h-[500px] object-cover object-top"
                />
              </div>
            </div>
          </div>

          {/* Observability and Monitoring */}
          <div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <img 
                  src="https://readdy.ai/api/search-image?query=Advanced%20observability%20and%20monitoring%20command%20center%20with%20large%20displays%20showing%20system%20metrics%2C%20application%20performance%20monitoring%20dashboards%2C%20log%20aggregation%20tools%2C%20distributed%20tracing%20visualizations%2C%20Grafana%20and%20Prometheus%20dashboards%2C%20real-time%20alerts%2C%20infrastructure%20health%20monitoring%2C%20professional%20NOC%20environment%20with%20teal%20accent%20lighting%2C%20high-tech%20monitoring%20station&width=800&height=600&seq=observability-monitoring-service-004&orientation=landscape"
                  alt="Observability and Monitoring"
                  className="rounded-2xl shadow-2xl w-full h-[500px] object-cover object-top"
                />
              </div>

              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/20 backdrop-blur-sm rounded-full border border-teal-400/30 mb-6">
                  <i className="ri-radar-line text-teal-400"></i>
                  <span className="text-sm text-teal-300">Observability</span>
                </div>
                
                <h3 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                  Observability &amp; Monitoring
                </h3>
                
                <p className="text-gray-300 text-lg leading-relaxed mb-6">
                  Gain complete visibility into your systems and applications with comprehensive observability and monitoring solutions. We help you implement robust monitoring strategies that provide real-time insights, proactive alerting, and deep performance analytics to ensure optimal system health and user experience.
                </p>

                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-teal-500/20 rounded-lg flex items-center justify-center mt-1 flex-shrink-0">
                      <i className="ri-check-line text-teal-400 text-sm"></i>
                    </div>
                    <div>
                      <strong className="text-white">Full-Stack Observability</strong>
                      <p className="text-gray-400 text-sm mt-1">End-to-end visibility across infrastructure, applications, and user experience.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-teal-500/20 rounded-lg flex items-center justify-center mt-1 flex-shrink-0">
                      <i className="ri-check-line text-teal-400 text-sm"></i>
                    </div>
                    <div>
                      <strong className="text-white">Metrics, Logs &amp; Traces</strong>
                      <p className="text-gray-400 text-sm mt-1">Implement comprehensive telemetry collection with Prometheus, ELK, and Jaeger.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-teal-500/20 rounded-lg flex items-center justify-center mt-1 flex-shrink-0">
                      <i className="ri-check-line text-teal-400 text-sm"></i>
                    </div>
                    <div>
                      <strong className="text-white">Custom Dashboards &amp; Visualization</strong>
                      <p className="text-gray-400 text-sm mt-1">Build intuitive dashboards that provide actionable insights at a glance.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-teal-500/20 rounded-lg flex items-center justify-center mt-1 flex-shrink-0">
                      <i className="ri-check-line text-teal-400 text-sm"></i>
                    </div>
                    <div>
                      <strong className="text-white">Intelligent Alerting &amp; Incident Response</strong>
                      <p className="text-gray-400 text-sm mt-1">Proactive monitoring with smart alerts that reduce noise and accelerate resolution.</p>
                    </div>
                  </div>
                </div>

                <button 
                  onClick={() => setIsModalOpen(true)}
                  className="px-6 py-3 bg-teal-500 text-gray-900 rounded-lg font-semibold hover:bg-teal-400 transition-all cursor-pointer whitespace-nowrap"
                >
                  Learn More
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-16">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Business Benefits
            </h2>
            <p className="text-gray-400 text-lg max-w-3xl mx-auto">
              Our consulting services deliver tangible results that drive business growth and operational excellence.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
              <div className="w-14 h-14 bg-teal-500/20 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-speed-up-line text-3xl text-teal-400"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Faster Time-to-Market</h3>
              <p className="text-gray-400">
                Accelerate product delivery with streamlined DevOps processes and automation, reducing deployment time by up to 80%.
              </p>
            </div>

            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
              <div className="w-14 h-14 bg-teal-500/20 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-3xl text-teal-400"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Enhanced Security</h3>
              <p className="text-gray-400">
                Protect your business from cyber threats with enterprise-grade security measures and proactive threat detection.
              </p>
            </div>

            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
              <div className="w-14 h-14 bg-teal-500/20 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-funds-line text-3xl text-teal-400"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Cost Optimization</h3>
              <p className="text-gray-400">
                Reduce operational costs through automation, efficient resource utilization, and optimized cloud infrastructure.
              </p>
            </div>

            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
              <div className="w-14 h-14 bg-teal-500/20 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-eye-line text-3xl text-teal-400"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Complete Visibility</h3>
              <p className="text-gray-400">
                Gain real-time insights into system performance and user behavior with comprehensive observability solutions.
              </p>
            </div>

            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
              <div className="w-14 h-14 bg-teal-500/20 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-line-chart-line text-3xl text-teal-400"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Competitive Advantage</h3>
              <p className="text-gray-400">
                Stay ahead of the curve with cutting-edge blockchain and crypto market expertise that opens new opportunities.
              </p>
            </div>

            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
              <div className="w-14 h-14 bg-teal-500/20 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-team-line text-3xl text-teal-400"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Team Empowerment</h3>
              <p className="text-gray-400">
                Upskill your team with knowledge transfer and best practices that build long-term internal capabilities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-950">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Transform Your Technology Strategy?
          </h2>
          <p className="text-xl text-gray-300 mb-10">
            Schedule a free consultation to discuss how our expert consulting services can help your business achieve its technology goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => setIsModalOpen(true)}
              className="px-8 py-4 bg-teal-500 text-gray-900 rounded-lg font-semibold hover:bg-teal-400 transition-all cursor-pointer whitespace-nowrap shadow-xl text-lg"
            >
              Schedule Free Consultation
            </button>
            <button 
              onClick={() => window.REACT_APP_NAVIGATE('/about')}
              className="px-8 py-4 bg-transparent text-white rounded-lg font-semibold border-2 border-gray-600 hover:bg-gray-800/50 transition-all cursor-pointer whitespace-nowrap text-lg"
            >
              Learn About Our Team
            </button>
          </div>
        </div>
      </section>

      <Footer />
      
      <ConsultationModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}